import React, {FC} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelExternalTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {
  ChannelIcon,
  ChannelIconSizesEnum,
} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import InboxFlourish from '../../../../assets/images/basicsGuide/inboxFlourish.svg';
import InboxOrganizationRoutingLines from '../../../../assets/images/basicsGuide/inboxOrganizationRoutingLines.svg';
import InboxToInboxRoutingLine from '../../../../assets/images/basicsGuide/inboxToInboxRoutingLine.svg';
import NoChannelLine from '../../../../assets/images/basicsGuide/noChannelLine.svg';
import {
  CardText,
  StyledInboxCard,
  StyledInboxIcon,
  StyledVisualContainer,
  StyledVisualDetailsText,
  StyledVisualLayout,
} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideInboxOrganizationVisualProps {}

/*
 * Style.
 */

const StyledMessageIcon = styled(Icon)`
  color: #808080;
`;

const StyledPriorityInboxIcon = styled(StyledInboxIcon)`
  color: #dd3c00;
`;

const StyledChannelCardsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  margin-right: 47px;
`;

const StyledSmallChannelCard = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  background: ${(p) => p.theme.greys.shade30};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const StyledChannelIcon = styled(ChannelIcon)`
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
`;

const StyledNoChannelCard = styled.div`
  width: 148px;
  height: 80px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  line-height: 13px;
  color: ${(p) => p.theme.greys.shade70};
  border: 2px dashed ${(p) => p.theme.greys.shade50};
`;

const StyledPriorityInboxCard = styled(StyledInboxCard)`
  animation-name: inboxOrganizationVisualMovePriorityInbox;
  animation-delay: 200ms;
  animation-duration: 1200ms;
  animation-fill-mode: forwards;

  position: absolute;
  top: 454px;
  left: 197px;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));

  @keyframes inboxOrganizationVisualMovePriorityInbox {
    0% {
      left: 197px;
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }

    ${(800 / 1200) * 100}% {
      left: 343px;
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }

    100% {
      left: 343px;
      filter: drop-shadow(0px 0px 10px rgba(255, 104, 51, 1));
    }
  }

  opacity: 0:
`;

const StyledSupportInboxCard = styled(StyledInboxCard)`
  animation-name: inboxOrganizationVisualMoveSupportInbox;
  animation-delay: 200ms;
  animation-duration: 600ms;
  animation-fill-mode: forwards;

  position: absolute;
  top: 248px;
  left: 197px;

  @keyframes inboxOrganizationVisualMoveSupportInbox {
    0% {
      top: 248px;
      left: 197px;
    }
    100% {
      top: 454px;
      left: 94px;
    }
  }
`;

const StyledChannelsContainer = styled.div`
  position: absolute;
  top: 248px;
  left: 40px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;

  ${computeFadeInAnimation('channels-container', 1000, 400)}
`;

const StyledInboxOrganizationRoutingLines = styled.img`
  position: absolute;
  top: 336px;
  left: 80px;

  ${computeFadeInAnimation('inbox-organization-routing-lines', 1000, 400)}
`;

const StyledInboxToInboxRoutingLine = styled.img`
  position: absolute;
  top: 491px;
  left: 249px;

  ${computeFadeInAnimation('inbox-to-inbox-routing-line', 1000, 400)}
`;

const StyledNoChannelLine = styled.img`
  position: absolute;
  top: 336px;
  left: 409px;

  ${computeFadeInAnimation('no-channel-line', 1000, 400)}
`;

const StyledMessageCard = styled.div`
  position: absolute;
  top: 479px;
  left: 272px;
  width: 40px;
  height: 40px;
  background: ${(p) => p.theme.greys.white};
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));

  ${computeFadeInAnimation('message-card', 1000, 400)}
`;

const StyledInboxFlourish = styled.img`
  position: absolute;
  top: 439px;
  left: 493px;
  z-index: 1;

  ${computeFadeInAnimation('inbox-flourish', 1000, 400)}
`;

const StyledInboxOrganizationDetailsText = styled(StyledVisualDetailsText)`
  animation-delay: 1000ms;
  animation-duration: 400ms;
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideInboxOrganizationVisual: FC<
  OnboardingBasicsGuideInboxOrganizationVisualProps
> = () => (
  <StyledVisualContainer>
    <StyledInboxFlourish src={InboxFlourish} alt="Front message routing!" />
    <StyledInboxOrganizationRoutingLines
      src={InboxOrganizationRoutingLines}
      alt="Organize your messages with Front!"
    />
    <StyledNoChannelLine src={NoChannelLine} alt="Organize your messages with Front!" />
    <StyledSupportInboxCard>
      <StyledInboxIcon name="inboxFilled" />
      <CardText>Support</CardText>
    </StyledSupportInboxCard>
    <StyledInboxToInboxRoutingLine src={InboxToInboxRoutingLine} alt="Organize your messages with Front!" />
    <StyledMessageCard>
      <StyledMessageIcon name="channelEmail" />
    </StyledMessageCard>
    <StyledPriorityInboxCard>
      <StyledPriorityInboxIcon name="inboxFilled" />
      <CardText>Support - Priority</CardText>
    </StyledPriorityInboxCard>
    <StyledChannelsContainer>
      <StyledChannelCardsContainer>
        <StyledSmallChannelCard>
          <StyledChannelIcon type={ChannelTypesEnum.GMAIL} size={ChannelIconSizesEnum.BIG} />
        </StyledSmallChannelCard>
        <StyledSmallChannelCard>
          <StyledChannelIcon type={ChannelTypesEnum.FACEBOOK} size={ChannelIconSizesEnum.BIG} />
        </StyledSmallChannelCard>
        <StyledSmallChannelCard>
          <StyledChannelIcon type={ChannelExternalTypesEnum.INSTAGRAM} size={ChannelIconSizesEnum.BIG} />
        </StyledSmallChannelCard>
      </StyledChannelCardsContainer>
      <StyledNoChannelCard>
        <Trans id="onboarding-basics-guide-inbox-organization-visual-no-channels">No channel</Trans>
      </StyledNoChannelCard>
    </StyledChannelsContainer>
    <StyledVisualLayout>
      <StyledInboxOrganizationDetailsText>
        <Trans id="onboarding-basics-guide-inbox-organization-visual-details">
          You can also create inboxes that aren’t connected to a channel. These inboxes give your team the
          ability to organize messages by topic, priority, team, etc…
        </Trans>
      </StyledInboxOrganizationDetailsText>
    </StyledVisualLayout>
  </StyledVisualContainer>
);

function computeFadeInAnimation(slug: string, delay: number, duration: number) {
  const animationName = `fadeIn-${slug}`;
  return `
    animation-name: ${animationName};
    animation-delay: ${delay}ms;
    animation-duration: ${duration}ms;
    animation-fill-mode: forwards;
    opacity: 0;

    @keyframes ${animationName} {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `;
}
